import React, { createContext, useEffect, useState } from 'react';
import { Socket } from 'socket.io-client';
import { logger } from '../utils/logger';
import { socketService } from '../services/unifiedSocketService';
import authService from '../services/authService';

export type CallStatus = 'IDLE' | 'RINGING' | 'IN_CALL' | 'ENDED';

// Définir le type Call pour le contexte d'appel
interface Call {
  id: string;
  callerId: string;
  receiverId: string;
  type: 'video' | 'audio';
  status?: string;
}

export interface CallContextType {
  callStatus: CallStatus;
  setCallStatus: (status: CallStatus) => void;
  socket: Socket | null;
  incomingCall: Call | null;
  setIncomingCall: (call: Call | null) => void;
}

export const CallContext = createContext<CallContextType | undefined>(undefined);

export const CallProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [callStatus, setCallStatus] = useState<CallStatus>('IDLE');
  const [socket, setSocket] = useState<Socket | null>(null);
  const [incomingCall, setIncomingCall] = useState<Call | null>(null);

  useEffect(() => {
    const token = authService.getToken();
    const user = authService.getUser();

    if (!token || !user?.id) {
      logger.debug('⏸️ [CallContext] Sans token, Socket.IO non initialisé');
      return;
    }

    let isMounted = true;
    const handleConnect = () => {
      if (!isMounted) return;
      setSocket(socketService.getSocket());
      logger.debug('✅ [CallContext] WebSocket connecté');
    };

    const handleIncomingCall = (call: Call) => {
      if (!isMounted) return;
      setIncomingCall(call);
      setCallStatus('RINGING');
    };

    const handleAccepted = () => {
      if (isMounted) setCallStatus('IN_CALL');
    };

    const handleEnded = () => {
      if (isMounted) setCallStatus('ENDED');
    };

    const handleRejected = () => {
      if (isMounted) setCallStatus('IDLE');
    };

    socketService.on('connect', handleConnect);
    socketService.on('call:incoming', handleIncomingCall);
    socketService.on('call:accepted', handleAccepted);
    socketService.on('call:ended', handleEnded);
    socketService.on('call:rejected', handleRejected);

    socketService
      .connect()
      .then(() => {
        if (!isMounted) return;
        setSocket(socketService.getSocket());
      })
      .catch((error) => {
        logger.warn(
          "⚠️ Erreur lors de l'initialisation CallContext Socket.IO, mode déconnecté:",
          error
        );
      });

    return () => {
      isMounted = false;
      socketService.off('connect', handleConnect);
      socketService.off('call:incoming', handleIncomingCall);
      socketService.off('call:accepted', handleAccepted);
      socketService.off('call:ended', handleEnded);
      socketService.off('call:rejected', handleRejected);
    };
  }, []);

  return (
    <CallContext.Provider
      value={{ callStatus, setCallStatus, socket, incomingCall, setIncomingCall }}
    >
      {children}
    </CallContext.Provider>
  );
};
